import type { Character } from '../domain/character.ts';
import type { Item } from '../content/types.ts';
import type { HealthBar } from '../rules/health.ts';
import { CRAWLER_HEALTH_BAR_SLOTS, healthBarSlotValue, maxHealth } from '../rules/health.ts';
import type { ManaPool } from '../rules/mana.ts';
import { maxMana } from '../rules/mana.ts';
import { evadeModifier } from '../rules/evade.ts';
import { resolveMovement } from '../rules/movement.ts';
import type { Movement } from '../rules/movement.ts';
import type { Mitigation } from '../rules/damage.ts';
import { NO_MITIGATION } from '../rules/damage.ts';
import type { Effect } from '../rules/effects.ts';
import type { EffectiveCharacter } from './effectiveCharacter.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';

/**
 * Every value that is calculated rather than stored. One place, so no screen
 * ever has to work a number out for itself and no two screens can disagree.
 */
export interface DerivedStats {
  healthBar: HealthBar;
  maxHealth: number;
  mana: ManaPool;
  evadeModifier: number;
  movement: Movement;
  mitigation: Mitigation;
}

/**
 * Every value here comes from the ONE resolved effective character. Nothing
 * below may reach past it to the raw character Stats: doing so is how a Buff
 * ends up changing the sheet in one place and not another.
 */
export function deriveStatsFor(effective: EffectiveCharacter): DerivedStats {
  const { character, modifiers: resolved } = effective;
  const enhanced = effective.enhancedStats;
  const conMod = effective.statMod('con');

  return {
    healthBar: {
      slotValue: healthBarSlotValue(conMod),
      maxSlots: CRAWLER_HEALTH_BAR_SLOTS,
      slotsRemaining: Math.min(character.healthBarSlotsRemaining, CRAWLER_HEALTH_BAR_SLOTS),
    },
    maxHealth: maxHealth(conMod),
    mana: { max: maxMana(enhanced.int) + resolved.maxMana, current: character.currentMana },
    evadeModifier: evadeModifier(effective.statMod('dex')) + resolved.evade,
    // Rules p.42: DR, resistance, vulnerability and immunity come from armour,
    // magic and Buffs. With no effects supplied a crawler has none of their own.
    mitigation: {
      damageResistance: NO_MITIGATION.damageResistance + resolved.damageResistance,
      elementalResistances: resolved.elementalResistances,
      vulnerabilities: resolved.vulnerabilities,
      immunities: resolved.immunities,
    },
    // Additive modifiers first, then multipliers such as Fatigued halving Move
    // (Rules p.45). Rounded down: the rules never grant part of a foot.
    movement: (() => {
      const base = resolveMovement(
        resolved.move !== 0 || resolved.step !== 0
          ? [{ label: 'Effects', moveFeet: resolved.move, stepFeet: resolved.step }]
          : [],
      );
      return {
        moveFeet: Math.floor(base.moveFeet * resolved.moveMultiplier),
        stepFeet: Math.floor(base.stepFeet * resolved.stepMultiplier),
      };
    })(),
  };
}

/**
 * Convenience for callers that hold a Character and a list of Effects.
 *
 * Pass `lookupItem` to include worn gear (RULE DCC-INV-002). Omitting it is not
 * a shortcut - it means "Buffs and Debuffs only" - so a rules test can derive
 * numbers without a catalogue while the application always supplies one.
 */
export function deriveStats(
  character: Character,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): DerivedStats {
  return deriveStatsFor(buildEffectiveCharacter(character, effects, lookupItem));
}
